import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../../api/client';
import { Button, StatusBadge } from '../common';
import { getUserName, formatDate, isOverdue } from '../../utils/formatters';
import { TODO_STATUS } from '../../constants';

/**
 * EditTodoModal - Full task editor with all fields and task notes
 */
const EditTodoModal = ({ todo, users, onClose, onSave, canEdit = true }) => {
  const [editedTodo, setEditedTodo] = useState({
    title: todo?.title || '',
    details: todo?.details || '',
    dueDate: todo?.dueDate?.split('T')[0] || '',
    status: todo?.status || TODO_STATUS.NOT_STARTED,
    assignees: todo?.assignees || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notes, setNotes] = useState(todo?.notes || []);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [noteError, setNoteError] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [dueDateHistory, setDueDateHistory] = useState([]);
  const [fullTodo, setFullTodo] = useState(null);
  const [activeTab, setActiveTab] = useState('task');

  // Fetch full todo data on mount (list endpoints don't include notes, history, topic, meeting)
  useEffect(() => {
    if (!todo?.id) return;
    setNotesLoading(true);
    api.get(`/todos/${todo.id}`)
      .then(data => {
        setNotes(data.notes || []);
        setDueDateHistory(data.dueDateHistory || []);
        setFullTodo(data);
      })
      .catch(err => console.error('Failed to load todo data:', err))
      .finally(() => setNotesLoading(false));
  }, [todo?.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/todos/${todo.id}`, {
        title: editedTodo.title,
        details: editedTodo.details,
        dueDate: editedTodo.dueDate,
        status: editedTodo.status,
        assignees: editedTodo.assignees,
      });
      onSave();
    } catch (error) {
      console.error('Failed to update todo:', error);
      setError('Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;
    setAddingNote(true);
    try {
      const note = await api.post(`/todos/${todo.id}/notes`, { content: newNoteContent.trim() });
      setNotes(prev => [note, ...prev]);
      setNewNoteContent('');
    } catch (error) {
      console.error('Failed to add note:', error);
      setNoteError('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const toggleAssignee = (userId) => {
    const newAssignees = editedTodo.assignees.includes(userId)
      ? editedTodo.assignees.filter(id => id !== userId)
      : [...editedTodo.assignees, userId];
    setEditedTodo({ ...editedTodo, assignees: newAssignees });
  };

  if (!todo) return null;

  return (
    <div className="modal-overlay">
      <div className="modal modal-xl">
        <div className="modal-header">
          <h2 className="modal-title">Edit Task</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-tabs">
          <button
            className={`modal-tab-btn${activeTab === 'task' ? ' active' : ''}`}
            onClick={() => setActiveTab('task')}
          >
            Task
          </button>
          <button
            className={`modal-tab-btn${activeTab === 'notes' ? ' active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            Notes{notes.length > 0 ? ` (${notes.length})` : ''}
          </button>
          <button
            className={`modal-tab-btn${activeTab === 'history' ? ' active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Due Date History{dueDateHistory.length > 0 ? ` (${dueDateHistory.length})` : ''}
          </button>
        </div>
        <div className="modal-body">
          {activeTab === 'task' && (
            canEdit ? (
              <div>
                <div className="form-group">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editedTodo.title}
                    onChange={(e) => setEditedTodo({ ...editedTodo, title: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Details</label>
                  <textarea
                    className="form-input"
                    value={editedTodo.details}
                    onChange={(e) => setEditedTodo({ ...editedTodo, details: e.target.value })}
                    rows={4}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={editedTodo.dueDate}
                    onChange={(e) => setEditedTodo({ ...editedTodo, dueDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-input"
                    value={editedTodo.status}
                    onChange={(e) => setEditedTodo({ ...editedTodo, status: e.target.value })}
                  >
                    <option value={TODO_STATUS.NOT_STARTED}>{TODO_STATUS.NOT_STARTED}</option>
                    <option value={TODO_STATUS.IN_PROGRESS}>{TODO_STATUS.IN_PROGRESS}</option>
                    <option value={TODO_STATUS.DONE}>{TODO_STATUS.DONE}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Assignees</label>
                  <div style={{
                    maxHeight: '150px',
                    overflowY: 'auto',
                    border: '1px solid var(--color-border)',
                    borderRadius: '4px',
                    padding: '0.5rem'
                  }}>
                    {users.map(u => (
                      <label key={u.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.25rem'
                      }}>
                        <input
                          type="checkbox"
                          checked={editedTodo.assignees.includes(u.id)}
                          onChange={() => toggleAssignee(u.id)}
                        />
                        {getUserName(u.id, users)}
                      </label>
                    ))}
                  </div>
                </div>
                {(todo.createdAt || fullTodo?.meeting) && (
                  <table className="data-table" style={{ marginTop: '1rem' }}>
                    <tbody>
                      {todo.createdAt && (
                        <tr>
                          <td style={{ fontWeight: 'bold', width: '150px' }}>Created</td>
                          <td>{new Date(todo.createdAt).toLocaleString()}</td>
                        </tr>
                      )}
                      {fullTodo?.meeting && (
                        <tr>
                          <td style={{ fontWeight: 'bold' }}>Meeting</td>
                          <td>{fullTodo.meeting.name}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
                {fullTodo?.topic && (
                  <div style={{ marginTop: '1rem' }}>
                    <h4 style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.4rem', color: 'var(--color-primary)' }}>
                      Related Topic
                    </h4>
                    <div style={{
                      padding: '0.75rem',
                      background: 'var(--color-surface)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '4px',
                      borderLeft: '3px solid var(--color-primary)'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.35rem', fontSize: '0.95rem' }}>
                        {fullTodo.topic.title}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>
                        From: {fullTodo.meeting?.name || 'Unknown Meeting'} · Submitted by {fullTodo.topic.creatorName}
                      </div>
                      {fullTodo.topic.description && (
                        <div
                          style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}
                          dangerouslySetInnerHTML={{ __html: fullTodo.topic.description }}
                        />
                      )}
                      {fullTodo.topic.discussionNotes && (
                        <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--color-border)' }}>
                          <div style={{ fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '0.35rem' }}>Discussion Notes:</div>
                          <div style={{
                            fontSize: '0.85rem',
                            color: 'var(--color-text)',
                            whiteSpace: 'pre-wrap',
                            background: 'var(--color-bg)',
                            padding: '0.5rem',
                            borderRadius: '4px'
                          }}>
                            {fullTodo.topic.discussionNotes}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', color: 'var(--color-primary-dark)' }}>
                  {editedTodo.title}
                </h3>
                <table className="data-table" style={{ marginBottom: '1.25rem' }}>
                  <tbody>
                    <tr>
                      <td style={{ fontWeight: 'bold', width: '150px' }}>Status</td>
                      <td>
                        <select
                          className="form-input"
                          value={editedTodo.status}
                          onChange={(e) => setEditedTodo({ ...editedTodo, status: e.target.value })}
                          style={{ maxWidth: '200px' }}
                        >
                          <option value={TODO_STATUS.NOT_STARTED}>{TODO_STATUS.NOT_STARTED}</option>
                          <option value={TODO_STATUS.IN_PROGRESS}>{TODO_STATUS.IN_PROGRESS}</option>
                          <option value={TODO_STATUS.DONE}>{TODO_STATUS.DONE}</option>
                        </select>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Assigned To</td>
                      <td>
                        {users.filter(u => editedTodo.assignees.includes(u.id)).map(u => getUserName(u.id, users)).join(', ') || 'Unassigned'}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 'bold' }}>Due Date</td>
                      <td>
                        {editedTodo.dueDate ? (
                          <span style={{
                            color: isOverdue(editedTodo.dueDate, editedTodo.status) ? 'var(--color-danger)' : 'inherit',
                            fontWeight: isOverdue(editedTodo.dueDate, editedTodo.status) ? 'bold' : 'normal'
                          }}>
                            {formatDate(editedTodo.dueDate)}
                            {isOverdue(editedTodo.dueDate, editedTodo.status) && ' ⚠️ OVERDUE'}
                          </span>
                        ) : 'No due date'}
                      </td>
                    </tr>
                    {todo.createdAt && (
                      <tr>
                        <td style={{ fontWeight: 'bold' }}>Created</td>
                        <td>{new Date(todo.createdAt).toLocaleString()}</td>
                      </tr>
                    )}
                    {fullTodo?.meeting && (
                      <tr>
                        <td style={{ fontWeight: 'bold' }}>Meeting</td>
                        <td>{fullTodo.meeting.name}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {editedTodo.details && (
                  <div style={{ marginBottom: fullTodo?.topic ? '1.25rem' : 0 }}>
                    <h4 style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.4rem', color: 'var(--color-primary)' }}>
                      Task Details
                    </h4>
                    <div style={{
                      padding: '0.75rem',
                      background: 'var(--color-bg)',
                      borderRadius: '4px',
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.9rem'
                    }}>
                      {editedTodo.details}
                    </div>
                  </div>
                )}
                {fullTodo?.topic && (
                  <div>
                    <h4 style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.4rem', color: 'var(--color-primary)' }}>
                      Related Topic
                    </h4>
                    <div style={{
                      padding: '0.75rem',
                      background: 'var(--color-surface)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '4px',
                      borderLeft: '3px solid var(--color-primary)'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.35rem', fontSize: '0.95rem' }}>
                        {fullTodo.topic.title}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>
                        From: {fullTodo.meeting?.name || 'Unknown Meeting'} · Submitted by {fullTodo.topic.creatorName}
                      </div>
                      {fullTodo.topic.description && (
                        <div
                          style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}
                          dangerouslySetInnerHTML={{ __html: fullTodo.topic.description }}
                        />
                      )}
                      {fullTodo.topic.discussionNotes && (
                        <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--color-border)' }}>
                          <div style={{ fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '0.35rem' }}>Discussion Notes:</div>
                          <div style={{
                            fontSize: '0.85rem',
                            color: 'var(--color-text)',
                            whiteSpace: 'pre-wrap',
                            background: 'var(--color-bg)',
                            padding: '0.5rem',
                            borderRadius: '4px'
                          }}>
                            {fullTodo.topic.discussionNotes}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          )}
          {activeTab === 'notes' && (
            <div>
              <div style={{ marginBottom: notes.length > 0 ? '1rem' : 0, paddingBottom: notes.length > 0 ? '1rem' : 0, borderBottom: notes.length > 0 ? '1px solid var(--color-border)' : 'none' }}>
                {noteError && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>{noteError}</div>}
                <label className="form-label">Add a Note</label>
                <textarea
                  className="form-input"
                  placeholder="Type your note here..."
                  value={newNoteContent}
                  onChange={(e) => setNewNoteContent(e.target.value)}
                  rows={3}
                  disabled={addingNote}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.4rem' }}>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={handleAddNote}
                    disabled={addingNote || !newNoteContent.trim()}
                  >
                    {addingNote ? 'Adding...' : 'Add Note'}
                  </Button>
                </div>
              </div>
              {notesLoading ? (
                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>Loading notes...</div>
              ) : notes.length > 0 ? (
                <div style={{
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}>
                  {notes.map((note, idx) => (
                    <div
                      key={note.id}
                      style={{
                        padding: '0.6rem 0.75rem',
                        borderBottom: idx < notes.length - 1 ? '1px solid var(--color-border)' : 'none',
                        fontSize: '0.875rem',
                      }}
                    >
                      <div style={{ whiteSpace: 'pre-wrap', marginBottom: '0.25rem' }}>{note.content}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                        {note.createdByName} &mdash; {new Date(note.createdAt).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}
          {activeTab === 'history' && (
            <div style={{ fontSize: '0.85rem' }}>
              {dueDateHistory.length > 0 ? (
                dueDateHistory.map((entry, idx) => (
                  <div key={idx} style={{ marginBottom: idx < dueDateHistory.length - 1 ? '0.5rem' : 0 }}>
                    <strong>{new Date(entry.changedAt).toLocaleString()}:</strong>{' '}
                    {entry.previousDate ? formatDate(entry.previousDate) : 'No date'} → {entry.date ? formatDate(entry.date) : 'No date'}
                    {entry.changedByName && ` (by ${entry.changedByName})`}
                  </div>
                ))
              ) : (
                <span style={{ color: 'var(--color-text-muted)' }}>No due date changes recorded.</span>
              )}
            </div>
          )}
        </div>
        {error && <div className="alert alert-error" style={{ margin: '0 0 0' }}>{error}</div>}
        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

EditTodoModal.propTypes = {
  todo: PropTypes.object,
  users: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  canEdit: PropTypes.bool,
};

export default EditTodoModal;

